import {
  Drawer,
  Card,
  Descriptions,
  Select,
  Button,
  message,
  Tag,
  Space,
} from "antd";
import { useState } from "react";
import {
  UserOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST = "Asia/Kolkata";

const { Option } = Select;

const NeedHelpDrawer = ({ activeRow, open, setOpen, onStatusUpdate }) => {
  const [status, setStatus] = useState(activeRow?.status || "IN_PROGRESS");
  const [loading, setLoading] = useState(false);

  const statusColors = {
    PENDING: "orange",
    IN_PROGRESS: "blue",
    RESOLVED: "green",
    CANCELLED: "red",
  };

  const handleStatusUpdate = async () => {
    if (!activeRow?.id) return;

    setLoading(true);
    try {
      await axiosInstance.post(`/v1/cre/need-help`, {
        id: activeRow.id,
        need_help: activeRow.help,
        custom_text: activeRow.custom_text,
        status: status,
      });
      message.success("Status updated successfully!");

      // Call the callback to refresh data in parent component
      if (onStatusUpdate) {
        onStatusUpdate();
      }
    } catch (error) {
      message.error("Failed to update status");
      console.error("Error updating status:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return dayjs.utc(dateString).tz(IST).format("DD-MM-YYYY HH:mm:ss");
  };

  return (
    <Drawer
      title={
        <Space>
          <UserOutlined />
          {`Help Request - ${activeRow?.user_id} - ${activeRow?.fullName}`}
        </Space>
      }
      width={"50vw"}
      onClose={() => setOpen(false)}
      open={open}
      styles={{
        body: {
          paddingBottom: 80,
        },
      }}
    >
      {activeRow && (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {/* User Information Card */}
          <Card
            title={
              <Space>
                <UserOutlined />
                User Information
              </Space>
            }
            size="small"
          >
            <Descriptions column={2} size="small">
              <Descriptions.Item label="User ID">
                <strong>{activeRow.user_id}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Full Name">
                <strong>{activeRow.fullName}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="User Type">
                <Tag color="blue">{activeRow.user_type}</Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Help Request Details Card */}
          <Card
            title={
              <Space>
                <InfoCircleOutlined />
                Help Request Details
              </Space>
            }
            size="small"
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Help Category">
                <Tag
                  color="purple"
                  style={{ fontSize: "14px", padding: "4px 8px" }}
                >
                  {activeRow.help}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                <div
                  style={{
                    background: "#f5f5f5",
                    padding: "12px",
                    borderRadius: "6px",
                    minHeight: "60px",
                  }}
                >
                  {activeRow.custom_text || "No description provided"}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Current Status">
                <Tag
                  color={statusColors[activeRow.status]}
                  style={{ fontSize: "12px" }}
                >
                  {activeRow.status.replace("_", " ")}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Timestamps Card */}
          <Card
            title={
              <Space>
                <CalendarOutlined />
                Timeline
              </Space>
            }
            size="small"
          >
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Submitted on">
                {formatDate(activeRow.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {formatDate(activeRow.updated_at)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Status Update Section */}
          <Card
            title="Update Status"
            size="small"
            extra={
              <Button
                type="primary"
                onClick={handleStatusUpdate}
                loading={loading}
                disabled={status === activeRow.status}
              >
                Update Status
              </Button>
            }
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                  }}
                >
                  Change Status:
                </label>
                <Select
                  value={status}
                  onChange={setStatus}
                  style={{ width: "200px" }}
                  size="large"
                >
                  <Option value="PENDING">
                    <Tag color="orange">PENDING</Tag>
                  </Option>
                  <Option value="IN_PROGRESS">
                    <Tag color="blue">IN PROGRESS</Tag>
                  </Option>
                  <Option value="RESOLVED">
                    <Tag color="green">RESOLVED</Tag>
                  </Option>
                  <Option value="CANCELLED">
                    <Tag color="red">CANCELLED</Tag>
                  </Option>
                </Select>
              </div>
              {status !== activeRow.status && (
                <div
                  style={{
                    background: "#fff7e6",
                    border: "1px solid #ffd591",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    fontSize: "12px",
                  }}
                >
                  Status will be changed from{" "}
                  <Tag color={statusColors[activeRow.status]} size="small">
                    {activeRow.status}
                  </Tag>
                  to{" "}
                  <Tag color={statusColors[status]} size="small">
                    {status}
                  </Tag>
                </div>
              )}
            </Space>
          </Card>
        </Space>
      )}
    </Drawer>
  );
};

export default NeedHelpDrawer;
